import React, { useEffect, useState, useRef, useCallback } from 'react';
import DisplayScreen from './DisplayScreen';
import { readInitialState, subscribeToState } from '../lib/conferenceStore';
import { builtInBackgrounds, defaultBackground } from '../data/backgrounds';
import { builtInTemplates } from '../data/templates';
import './PresenterWindow.css';

const RESUME_COUNTDOWN_START = 3; // seconds shown before window closes

const fallbackState = {
  title: builtInTemplates['session-starting'].title,
  paragraph: builtInTemplates['session-starting'].paragraph,
  lowerText: builtInTemplates['session-starting'].lower_text,
  animationEnabled: true,
  timerEnabled: false,
  targetTime: '09:33',
  targetTimezone: 'local',
  timerLabel: 'Session resumes in',
  manualMode: false,
  manualCurrentTime: '16:36',
  autoCloseOnTimerEnd: true,
  backgroundKey: defaultBackground,
  backgroundUrl: builtInBackgrounds[defaultBackground].url,
};

export const PresenterWindow = () => {
  const [state, setState] = useState(() => readInitialState() || fallbackState);
  const [resumeCountdown, setResumeCountdown] = useState(null); // null | 3 | 2 | 1 | 0
  const triggeredRef = useRef(false);

  useEffect(() => {
    const unsub = subscribeToState((next) => setState(next));
    document.title = 'IRI Presenter — Live Display';
    return unsub;
  }, []);

  const handleTimerZero = useCallback(() => {
    if (!state.autoCloseOnTimerEnd) return;
    if (triggeredRef.current) return;
    triggeredRef.current = true;
    setResumeCountdown(RESUME_COUNTDOWN_START);
  }, [state.autoCloseOnTimerEnd]);

  // Drive the "Resuming in 3, 2, 1..." countdown then close
  useEffect(() => {
    if (resumeCountdown === null) return;
    if (resumeCountdown <= 0) {
      const closeTimer = setTimeout(() => {
        try {
          window.close();
        } catch (e) {
          // ignore
        }
        // Fallback if window.close() was blocked
        setTimeout(() => {
          document.body.innerHTML =
            '<div style="display:flex;align-items:center;justify-content:center;height:100vh;background:#000;color:#5eead4;font-family:Inter,sans-serif;font-size:14px;letter-spacing:0.1em;text-transform:uppercase;">Session resumed — you may close this window</div>';
        }, 400);
      }, 600);
      return () => clearTimeout(closeTimer);
    }
    const tick = setTimeout(() => setResumeCountdown((c) => c - 1), 1000);
    return () => clearTimeout(tick);
  }, [resumeCountdown]);

  const backgroundUrl = state.backgroundUrl
    || builtInBackgrounds[state.backgroundKey]?.url
    || builtInBackgrounds[defaultBackground].url;

  const showOverlay = resumeCountdown !== null;
  const overlayNumber = resumeCountdown !== null && resumeCountdown > 0 ? resumeCountdown : null;
  const overlayDone = resumeCountdown !== null && resumeCountdown <= 0;

  return (
    <div className="presenter-window" data-testid="presenter-window">
      <DisplayScreen
        title={state.title}
        paragraph={state.paragraph}
        lowerText={state.lowerText}
        animationEnabled={state.animationEnabled}
        timerEnabled={state.timerEnabled}
        targetTime={state.targetTime}
        targetTimezone={state.targetTimezone || 'local'}
        timerLabel={state.timerLabel}
        manualMode={state.manualMode}
        manualCurrentTime={state.manualCurrentTime}
        backgroundUrl={backgroundUrl}
        onTimerZero={handleTimerZero}
      />

      {showOverlay && (
        <div className="resume-overlay" data-testid="resume-overlay">
          <div className="resume-overlay-bg" />
          <div className="resume-overlay-content">
            <div className="resume-pulse-wrapper">
              <span className="resume-pulse-ring" />
              <span className="resume-pulse-ring resume-pulse-ring-delay" />
              <span className="resume-pulse-core" />
            </div>
            <div className="resume-label">
              {overlayDone ? 'Resuming Now' : 'Resuming In'}
            </div>
            {overlayNumber !== null && (
              <div className="resume-number" key={overlayNumber} data-testid="resume-number">
                {overlayNumber}
              </div>
            )}
            {overlayDone && (
              <div className="resume-done-bar">
                <div className="resume-done-fill" />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PresenterWindow;
